/**
 * Editor-page adapter hook.
 *
 * Phase 5:
 *   - Reads the auto-start signal (URL `?onboarding_template=` then
 *     blank-canvas detection) once, when the EditorPage mounts.
 *   - Surfaces a ready-to-render `overlay` ReactNode that the host
 *     drops into JSX. The overlay portals itself to <body>, so its
 *     position in EditorPage's tree doesn't matter — anywhere inside
 *     the provider works.
 */

import { useEffect, useMemo, useRef, type ReactNode } from "react";
import type { Data } from "@puckeditor/core";
import { useOnboardingMentor } from "./OnboardingMentorProvider";
import type { StepContext } from "./editor/types";
import { EditorMentorOverlay } from "./editor/EditorMentorOverlay";
import {
  clearOnboardingParam,
  decideAutoStart,
} from "./editor/branchDetection";

export interface UseEditorMentorInput {
  data: Data | null;
  pageTitle: string | null;
  pagePublished: boolean;
  isDirty: boolean;
  historyOpenedCount: number;
  previewOpenedCount: number;
  canPublish: boolean;
  showSeoPanel: boolean;
  showVersions: boolean;

  setShowSeoPanel: (open: boolean) => void;
  setShowVersions: (open: boolean) => void;
  invokeSave: () => void;
  invokePreview: () => Window | null;
  invokePublish: () => void;
  navigateToNewPage: () => void;
  /** True when the most recent publish attempt this session errored. */
  publishError: boolean;
}

export interface UseEditorMentorResult {
  enabled: boolean;
  overlay: ReactNode;
}

export function useEditorMentor(
  input: UseEditorMentorInput,
): UseEditorMentorResult {
  const mentor = useOnboardingMentor();

  // Auto-start guard: fire once per mount, only when the provider is
  // active and there is no in-flight session for this user. A user who
  // already started + skipped will see status === "skipped" and we
  // intentionally do not bother them again. They can re-open from the
  // Profile page (Phase 6).
  const autoStartAttemptedRef = useRef(false);
  useEffect(() => {
    if (!mentor.enabled) return;
    if (autoStartAttemptedRef.current) return;
    // Wait until we actually have page data so the blank-state check
    // is reliable.
    if (input.data === null) return;
    autoStartAttemptedRef.current = true;

    const existing = mentor.session;
    // Resume path: an active or paused session — let it ride.
    if (existing && existing.status === "active") return;

    // Terminal/abandoned statuses ⇒ user already made a choice. Don't
    // re-prompt automatically.
    if (
      existing &&
      (existing.status === "skipped" ||
        existing.status === "dismissed" ||
        existing.status === "completed")
    ) {
      // Still consume any URL hint so a refresh doesn't show it.
      clearOnboardingParam();
      return;
    }

    const decision = decideAutoStart({
      search: typeof window !== "undefined" ? window.location.search : "",
      data: input.data,
    });

    if (!decision.shouldStart) {
      clearOnboardingParam();
      return;
    }

    mentor.start({
      branch: decision.branch,
      templateId: decision.templateId,
    });
    clearOnboardingParam();
  }, [mentor, input.data]);

  const session = mentor.session;
  const enabled = mentor.enabled && session !== null;

  const ctx = useMemo<StepContext | null>(() => {
    if (!session) {
      return null;
    }

    return {
      data: input.data,
      pageTitle: input.pageTitle,
      pagePublished: input.pagePublished,
      isDirty: input.isDirty,
      historyOpenedCount: input.historyOpenedCount,
      previewOpenedCount: input.previewOpenedCount,
      session: null,
      canPublish: input.canPublish,
      showSeoPanel: input.showSeoPanel,
      showVersions: input.showVersions,
      branch: session.branch ?? "unknown",
      templateId: session.templateId ?? null,
      setShowSeoPanel: input.setShowSeoPanel,
      setShowVersions: input.setShowVersions,
      invokeSave: input.invokeSave,
      invokePreview: input.invokePreview,
      invokePublish: input.invokePublish,
      navigateToNewPage: input.navigateToNewPage,
      publishError: input.publishError,
    };
  }, [
    input.canPublish,
    input.data,
    input.historyOpenedCount,
    input.invokePreview,
    input.invokePublish,
    input.invokeSave,
    input.isDirty,
    input.navigateToNewPage,
    input.pagePublished,
    input.pageTitle,
    input.previewOpenedCount,
    input.publishError,
    input.setShowSeoPanel,
    input.setShowVersions,
    input.showSeoPanel,
    input.showVersions,
    session,
  ]);

  const overlay = useMemo(() => {
    if (!enabled || !ctx) {
      return null;
    }

    return <EditorMentorOverlay ctx={ctx} />;
  }, [ctx, enabled]);

  return {
    enabled,
    overlay,
  };
}
